/**
 * Guardrail Watcher
 * 
 * Continuously monitors the codebase for changes and triggers
 * automatic re-analysis to keep guardrails up-to-date.
 */

import * as chokidar from 'chokidar';
import * as fs from 'fs/promises';
import * as path from 'path';
import type { 
  GuardrailConfig, 
  WatcherEvent, 
  RefreshResult,
  TruthpackData 
} from '../types.js';
import { scanTruthpack, saveTruthpack, loadTruthpack } from '../scanners/index.js';
import { forge } from '../forge/index.js';

export interface WatcherOptions {
  projectRoot: string;
  config: GuardrailConfig;
  onRefresh?: (result: RefreshResult) => void;
  onError?: (error: Error) => void;
  debounceMs?: number;
}

export class GuardrailWatcher {
  private projectRoot: string;
  private config: GuardrailConfig;
  private watcher: chokidar.FSWatcher | null = null;
  private debounceTimer: NodeJS.Timeout | null = null;
  private debounceMs: number;
  private pendingChanges: WatcherEvent[] = [];
  private lastTruthpackHash: string | null = null;
  private isRefreshing = false;
  private onRefresh?: (result: RefreshResult) => void;
  private onError?: (error: Error) => void;

  constructor(options: WatcherOptions) {
    this.projectRoot = options.projectRoot;
    this.config = options.config;
    this.debounceMs = options.debounceMs || 5000;
    this.onRefresh = options.onRefresh;
    this.onError = options.onError;
  }

  /**
   * Start watching for changes
   */
  async start(): Promise<void> {
    console.log('👁️ Guardrail Watcher starting...');

    // Load initial truthpack hash
    const truthpack = await loadTruthpack(this.projectRoot);
    this.lastTruthpackHash = truthpack?.meta.hash || null;

    // Create watcher
    this.watcher = chokidar.watch([
      '**/*.ts',
      '**/*.tsx',
      '**/*.js',
      '**/*.jsx',
      '**/*.json',
      '**/*.md',
      '.env*',
    ], {
      cwd: this.projectRoot,
      ignored: [
        '**/node_modules/**',
        '**/dist/**',
        '**/build/**',
        '**/.next/**',
        '**/.git/**',
        '**/.vibecheck/**', // Don't watch our own output
        '**/.cursor/**',    // Don't watch cursor rules
      ],
      persistent: true,
      ignoreInitial: true,
    });

    this.watcher.on('add', (filePath) => this.handleChange('add', filePath));
    this.watcher.on('change', (filePath) => this.handleChange('change', filePath));
    this.watcher.on('unlink', (filePath) => this.handleChange('unlink', filePath));
    this.watcher.on('error', (error) => this.handleError(error));

    console.log('👁️ Watching for changes...');

    // Do initial refresh based on configured interval
    if (this.config.settings.autoRefresh) {
      this.schedulePeriodicRefresh();
    }
  }

  /**
   * Stop watching
   */
  async stop(): Promise<void> {
    if (this.watcher) {
      await this.watcher.close();
      this.watcher = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    console.log('👁️ Watcher stopped');
  }

  /**
   * Force a refresh now
   */
  async forceRefresh(): Promise<RefreshResult> {
    return this.doRefresh();
  }

  private handleChange(type: WatcherEvent['type'], filePath: string): void {
    this.pendingChanges.push({
      type,
      path: filePath,
      timestamp: new Date(),
    });

    // Debounce changes
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.processChanges();
    }, this.debounceMs);
  }

  private async processChanges(): Promise<void> {
    if (this.isRefreshing) {
      return; // Skip if already refreshing
    }

    const changes = [...this.pendingChanges];
    this.pendingChanges = [];

    if (changes.length === 0) {
      return;
    }

    // Categorize changes
    const categories = this.categorizeChanges(changes);
    
    console.log(`📝 Processing ${changes.length} changes...`);
    console.log(`   - Source files: ${categories.source}`);
    console.log(`   - Config files: ${categories.config}`);
    console.log(`   - Documentation: ${categories.docs}`);

    // Decide what to refresh based on changes
    if (categories.source > 0 || categories.config > 0) {
      await this.doRefresh();
    } else if (categories.docs > 0) {
      // Only refresh documentation-related rules
      await this.refreshDocRules();
    }
  }

  private categorizeChanges(changes: WatcherEvent[]): {
    source: number;
    config: number;
    docs: number;
  } {
    let source = 0;
    let config = 0;
    let docs = 0;

    for (const change of changes) {
      const ext = path.extname(change.path).toLowerCase();
      const basename = path.basename(change.path).toLowerCase();

      if (['.ts', '.tsx', '.js', '.jsx'].includes(ext)) {
        source++;
      } else if (basename.includes('.env') || ext === '.json') {
        config++;
      } else if (ext === '.md') {
        docs++;
      }
    }

    return { source, config, docs };
  }

  private async doRefresh(): Promise<RefreshResult> {
    this.isRefreshing = true;
    const startTime = Date.now();

    try {
      console.log('🔄 Refreshing guardrails...');

      // Rescan truthpack
      const newTruthpack = await scanTruthpack(this.projectRoot, this.config.scanners);
      await saveTruthpack(this.projectRoot, newTruthpack);

      // Compare with previous
      const oldHash = this.lastTruthpackHash;
      const newHash = newTruthpack.meta.hash;
      this.lastTruthpackHash = newHash;

      // If truthpack changed significantly, regenerate rules
      let rulesUpdated = 0;
      let rulesAdded = 0;
      let rulesRemoved = 0;

      if (oldHash !== newHash) {
        console.log('📊 Truthpack changed, regenerating rules...');
        
        // Get old rules count
        const oldRulesDir = path.join(this.projectRoot, '.cursor', 'rules');
        let oldRulesCount = 0;
        try {
          const oldFiles = await fs.readdir(oldRulesDir);
          oldRulesCount = oldFiles.filter(f => f.endsWith('.mdc')).length;
        } catch {
          // Dir doesn't exist
        }

        // Regenerate
        const forgeResult = await forge(this.projectRoot, this.config);
        
        rulesUpdated = Math.min(oldRulesCount, forgeResult.rules.length);
        rulesAdded = Math.max(0, forgeResult.rules.length - oldRulesCount);
        rulesRemoved = Math.max(0, oldRulesCount - forgeResult.rules.length);
      }

      // Learn new conventions
      const conventionsLearned: string[] = [];
      const patternsDetected: string[] = [];

      const duration = Date.now() - startTime;
      
      const result: RefreshResult = {
        rulesUpdated,
        rulesAdded,
        rulesRemoved,
        conventionsLearned,
        patternsDetected,
        duration,
      };

      console.log(`✅ Refresh complete in ${duration}ms`);
      console.log(`   - Rules: +${rulesAdded} / -${rulesRemoved} / ~${rulesUpdated}`);

      if (this.onRefresh) {
        this.onRefresh(result);
      }

      return result;
    } catch (error) {
      this.handleError(error as Error);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  private async refreshDocRules(): Promise<void> {
    // Lightweight refresh for documentation changes only
    console.log('📄 Refreshing documentation rules...');
    // This could be expanded to just update docguard-related rules
  }

  private schedulePeriodicRefresh(): void {
    const intervalMs = this.config.settings.refreshInterval;
    
    console.log(`⏰ Periodic refresh scheduled every ${intervalMs / 1000}s`);
    
    setInterval(async () => {
      if (!this.isRefreshing && this.pendingChanges.length === 0) {
        console.log('⏰ Running periodic refresh...');
        try {
          await this.doRefresh();
        } catch (error) {
          // Error already handled
        }
      }
    }, intervalMs);
  }

  private handleError(error: Error): void {
    console.error('❌ Watcher error:', error.message);
    if (this.onError) {
      this.onError(error);
    }
  }
}

/**
 * Create and start a watcher
 */
export async function startWatcher(options: WatcherOptions): Promise<GuardrailWatcher> {
  const watcher = new GuardrailWatcher(options);
  await watcher.start();
  return watcher;
}

/**
 * Quick one-time refresh
 */
export async function refreshGuardrails(
  projectRoot: string, 
  config: GuardrailConfig
): Promise<RefreshResult> {
  const watcher = new GuardrailWatcher({ projectRoot, config });
  return watcher.forceRefresh();
}
